
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import {orderService} from "@/services/orderService";

interface StatRow { period: string; count: number; }
interface StatsData {
  by_day: StatRow[];
  by_week: StatRow[];
  by_month: StatRow[];
}
interface ChartBar { label: string; shortLabel: string; fullLabel: string; count: number; heightPct: number; isCurrent: boolean; }

@Component({ name: 'OrderStatsDashboard' })
export default class OrderStatsDashboard extends Vue {
  @Prop({ required: true }) filters!: object;

  protected loading: boolean = false;
  protected groupBy: 'day' | 'week' | 'month' = 'day';
  protected stats: StatsData = { by_day: [], by_week: [], by_month: [] };

  protected get currentRows(): StatRow[] {
    if (this.groupBy === 'month') return this.stats.by_month;
    if (this.groupBy === 'week') return this.stats.by_week;
    return this.stats.by_day;
  }

  protected get total(): number {
    return this.currentRows.reduce(function (sum, row) { return sum + row.count; }, 0);
  }

  private isoWeekPeriod(date: Date): string {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    const weekStr = week < 10 ? '0' + week : '' + week;
    return d.getUTCFullYear() + '-W' + weekStr;
  }

  protected get currentPeriod(): string {
    const now = new Date();
    if (this.groupBy === 'month') return now.toISOString().slice(0, 7);
    if (this.groupBy === 'week') return this.isoWeekPeriod(now);
    return now.toISOString().slice(0, 10);
  }

  protected get cutoffPeriod(): string {
    const now = new Date();
    if (this.groupBy === 'day') {
      const cutoff = new Date(now.getTime() - 62 * 24 * 3600000);
      return cutoff.toISOString().slice(0, 10);
    }
    if (this.groupBy === 'week') {
      const cutoff = new Date(now.getTime() - 364 * 24 * 3600000);
      return this.isoWeekPeriod(cutoff);
    }
    const cutoff = new Date(now.getFullYear(), now.getMonth() - 12, 1);
    const m = cutoff.getMonth() + 1;
    return cutoff.getFullYear() + '-' + (m < 10 ? '0' + m : '' + m);
  }

  protected get timeLimitLabel(): string {
    if (this.groupBy === 'day') return this.$t('orders.order.stats.last2Months').toString();
    return this.$t('orders.order.stats.lastYear').toString();
  }

  protected get chartBars(): ChartBar[] {
    const cutoff = this.cutoffPeriod;
    const rows = this.currentRows.filter(function (r) { return r.period >= cutoff; }).slice().reverse();
    if (rows.length === 0) return [];
    const max = Math.max.apply(null, rows.map(function (r) { return r.count; }));
    const groupBy = this.groupBy;
    const currentPeriod = this.currentPeriod;
    return rows.map(function (row) {
      let shortLabel: string;
      let fullLabel: string;
      if (groupBy === 'week') {
        shortLabel = row.period.slice(5);
        fullLabel = row.period.slice(5) + ' · ' + row.period.slice(0, 4);
      } else if (groupBy === 'month') {
        shortLabel = row.period.slice(5) + '/' + row.period.slice(2, 4);
        fullLabel = row.period.slice(5) + '/' + row.period.slice(0, 4);
      } else {
        shortLabel = row.period.slice(8) + '/' + row.period.slice(5, 7);
        fullLabel = row.period.slice(8) + '/' + row.period.slice(5, 7) + '/' + row.period.slice(0, 4);
      }
      return {
        label: row.period,
        shortLabel: shortLabel,
        fullLabel: fullLabel,
        count: row.count,
        heightPct: max > 0 ? Math.round((row.count / max) * 100) : 0,
        isCurrent: row.period === currentPeriod,
      };
    });
  }

  @Watch('filters', { deep: true, immediate: true })
  protected onFiltersChanged() {
    this.loadStats();
  }

  private loadStats() {
    this.loading = true;
    orderService.fetchOrderStatistics(this.filters).then((data: StatsData) => {
      this.stats = data;
      this.loading = false;
    }).catch(() => {
      this.loading = false;
    });
  }
}
